import { ApiError, client } from "@lib/api"
import { useTranslation } from "react-i18next"
import { useState } from "react"
import { TonWalletUtils } from "@lib/secrets"
import * as Sentry from "@sentry/react"
import { CaptchaRequiredResponse } from "../types/captcha"

export type Destination = 'bank' | 'e-wallet' | 'pay-bank'

type InitiateRedemptionRequestParams = {
    ui_amount: number,
    destination_uuid: string,
    destination: Destination,
    memo?: string,
    captcha_key?: string,
    captcha_value?: { slide_x: number, slide_y: number }
}

type InitiateP2PTransferParams = {
    ui_amount: number
    destination_address: string
    address_name?: string
    save_destination?: boolean
}

type InitiatedRedemptionRequestResponse = {
    captcha_required?: boolean
    redeemRequestUuid: string
    seqNo: number
    serializedTransaction: string
}

type SentRedemptionRequestResponse = {
    uuid: string
    status: string
    activity: {
        uuid: string
    }
}

type InitiatedP2PTransferResponse = {
    uuid: string
    seqNo: number
    serializedTransaction: string
}

type SentP2PTransferResponse = {
    signature: string,
    amount: number,
    message: string,
    uuid: string,
    activity: {
        uuid: string
    }
}

const useManageTransactionRequest = () => {
    const { t } = useTranslation()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [data, setData] = useState<any>(null)

    const initiateRedemptionRequest = async ({
        ui_amount,
        destination,
        destination_uuid,
        memo,
        captcha_key,
        captcha_value
    }: InitiateRedemptionRequestParams): Promise<InitiatedRedemptionRequestResponse | CaptchaRequiredResponse> => {
        try {
            const response = await client.doPost('/v2/wallet/redeem/initiate', {
                ui_amount,
                destination,
                destination_uuid,
                memo,
                ...(captcha_key && captcha_value && {
                    captcha_key,
                    captcha_value
                })
            })

            // Check if response contains captcha requirement (explicit flag)
            if (response && response.captcha_required) {
                return response as CaptchaRequiredResponse
            }

            // Check if this is a captcha validation failure response (implicit format)
            if (response && response.captcha_key && response.master_image_base64 && response.thumb_image_base64) {
                return {
                    captcha_required: true,
                    data: response
                } as CaptchaRequiredResponse
            }

            return response as InitiatedRedemptionRequestResponse
        } catch (error: any) {
            // Check if this is an API error that might contain captcha data
            if (error instanceof ApiError && error.statusCode >= 400 && error.responseBody) {
                const responseBody = error.responseBody;

                // Check if error response contains captcha requirement (explicit flag)
                if (responseBody && typeof responseBody === 'object' && responseBody.captcha_required) {
                    return responseBody as CaptchaRequiredResponse
                }

                // Check if error response contains captcha data (implicit format)
                if (responseBody && typeof responseBody === 'object' && responseBody.captcha_key && responseBody.master_image_base64 && responseBody.thumb_image_base64) {
                    return {
                        captcha_required: true,
                        data: responseBody
                    } as CaptchaRequiredResponse
                }
            }

            throw error
        }
    }

    const sendBankSerializedTransaction = async (data: string, uuid: string): Promise<SentRedemptionRequestResponse> => {
        try {
            return await client.doPost('/v2/wallet/redeem-to-bank/send', {
                uuid,
                data
            })
        } catch (error) {
            throw error
        }
    }

    const sendEWalletSerializedTransaction = async (data: string, uuid: string): Promise<SentRedemptionRequestResponse> => {
        try {
            return await client.doPost('/v2/wallet/redeem-to-ewallet/send', {
                uuid,
                data
            })
        } catch (error) {
            throw error
        }
    }

    const sendPaymentBankSerializedTransaction = async (data: string, uuid: string) => {
        try {
            return await client.doPost('/v2/wallet/pay/bank_transfer/send', {
                uuid,
                data
            })
        } catch (error) {
            throw error
        }
    }

    const submitPpobEscrowDeposit = async (signedTx: string, escrowUuid: string) => {
        try {
            return await client.doPost('/wallet/escrow/submit-deposit', {
                escrowUuid,
                signedTx
            })
        } catch (error) {
            throw error
        }
    }

    const requestBankRedemption = async (params: InitiateRedemptionRequestParams) => {
        try {
            setIsLoading(true)
            const initiateResponse = await initiateRedemptionRequest(params)

            // Check if captcha is required
            if (initiateResponse.captcha_required === true) {
                return initiateResponse as CaptchaRequiredResponse
            }

            const { redeemRequestUuid, seqNo, serializedTransaction } = initiateResponse as InitiatedRedemptionRequestResponse
            const signedTransaction = await TonWalletUtils.signMessage(serializedTransaction, seqNo)

            const response = await sendBankSerializedTransaction(signedTransaction, redeemRequestUuid)
            setData(response)
            return response
        } catch (error: unknown) {
            Sentry.captureException(error)
            let errorMessage
            if (error instanceof ApiError && error.errorCode) {
                if (error.errorCode === 2015) {
                    throw error
                }
                // Check if translation exists for this error code, otherwise use generic message
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                // If the translation key doesn't exist, t() will return the key itself
                errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
            } else {
                errorMessage = t('errors.genericClientDescription')
            }
            setError(errorMessage)
            throw errorMessage
        } finally {
            setIsLoading(false)
        }
    }

    const requestEWalletRedemption = async (params: InitiateRedemptionRequestParams) => {
        try {
            setIsLoading(true)
            const initiateResponse = await initiateRedemptionRequest(params)

            // Check if captcha is required
            if (initiateResponse.captcha_required === true) {
                return initiateResponse as CaptchaRequiredResponse
            }

            const { redeemRequestUuid, seqNo, serializedTransaction } = initiateResponse as InitiatedRedemptionRequestResponse
            const signedTransaction = await TonWalletUtils.signMessage(serializedTransaction, seqNo)

            const response = await sendEWalletSerializedTransaction(signedTransaction, redeemRequestUuid)
            setData(response)
            return response
        } catch (error: unknown) {
            Sentry.captureException(error)
            let errorMessage
            if (error instanceof ApiError && error.errorCode) {
                if (error.errorCode === 2015) {
                    throw error
                }
                // Check if translation exists for this error code, otherwise use generic message
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                // If the translation key doesn't exist, t() will return the key itself
                errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
            } else {
                errorMessage = t('errors.genericClientDescription')
            }
            setError(errorMessage)
            throw errorMessage
        } finally {
            setIsLoading(false)
        }
    }

    const requestPaymentBankRedemption = async (params: InitiateRedemptionRequestParams) => {
        try {
            setIsLoading(true)
            const initiateResponse = await initiateRedemptionRequest(params)

            // Check if captcha is required
            if (initiateResponse.captcha_required === true) {
                return initiateResponse as CaptchaRequiredResponse
            }

            const { redeemRequestUuid, seqNo, serializedTransaction } = initiateResponse as InitiatedRedemptionRequestResponse
            const signedTransaction = await TonWalletUtils.signMessage(serializedTransaction, seqNo)
            const response = await sendPaymentBankSerializedTransaction(signedTransaction, redeemRequestUuid)
            setData(response)
            return response
        } catch (error: unknown) {
            Sentry.captureException(error)
            let errorMessage
            if (error instanceof ApiError && error.errorCode) {
                // Check if translation exists for this error code, otherwise use generic message
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                // If the translation key doesn't exist, t() will return the key itself
                errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
            } else {
                errorMessage = t('errors.genericClientDescription')
            }
            setError(errorMessage)
            throw errorMessage
        } finally {
            setIsLoading(false)
        }
    }

    const initiateP2PTransfer = async (params: InitiateP2PTransferParams): Promise<InitiatedP2PTransferResponse> => {
        try {
            return await client.doPost('/v2/wallet/transfer/initiate', params)
        } catch (error) {
            throw error
        }
    }

    const sendP2PTransferSerializedTransaction = async (data: string, uuid: string, save_destination?: boolean, address_name?: string): Promise<SentP2PTransferResponse> => {
        try {
            return await client.doPost('/v2/wallet/transfer/send', { data, uuid, save_destination, address_name })
        } catch (error) {
            throw error
        }
    }

    const requestP2PTransfer = async (params: InitiateP2PTransferParams) => {
        try {
            setIsLoading(true)
            const { uuid, seqNo, serializedTransaction } = await initiateP2PTransfer(params)

            const signedTransaction = await TonWalletUtils.signMessage(serializedTransaction, seqNo)

            const response = await sendP2PTransferSerializedTransaction(signedTransaction, uuid, params.save_destination, params.address_name)
            setData(response)
            return response
        } catch (error: unknown) {
            Sentry.captureException(error)
            let errorMessage
            if (error instanceof ApiError && error.errorCode) {
                // Check if translation exists for this error code, otherwise use generic message
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                // If the translation key doesn't exist, t() will return the key itself
                errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
            } else {
                errorMessage = t('errors.genericClientDescription')
            }
            setError(errorMessage)
            throw errorMessage
        } finally {
            setIsLoading(false)
        }
    }

    const requestPrepaidPayment = async (
        buyerSkuCode: string,
        customerNo: string,
        captchaData?: {
            captcha_key: string,
            captcha_value: { slide_x: number, slide_y: number }
        }
    ) => {
        try {
            setIsLoading(true)

            // Step 1: Create prepaid order and initiate escrow
            const initiateResponse = await client.doPost('/wallet/ppob/prepaid/orders', {
                buyer_sku_code: buyerSkuCode,
                customer_no: customerNo,
                ...(captchaData && {
                    captcha_key: captchaData.captcha_key,
                    captcha_value: captchaData.captcha_value
                })
            })

            // Check if response contains captcha requirement
            if (initiateResponse && initiateResponse.captcha_required) {
                return initiateResponse as CaptchaRequiredResponse
            }

            // Check if this is a captcha validation failure response (implicit format)
            if (initiateResponse && initiateResponse.captcha_key && initiateResponse.master_image_base64 && initiateResponse.thumb_image_base64) {
                return {
                    captcha_required: true,
                    data: initiateResponse
                } as CaptchaRequiredResponse
            }

            const { escrow } = initiateResponse.data
            const { escrowUuid, unsignedTx } = escrow
            const seqNo = escrow.additionalData.seqNo

            // Step 2: Sign the transaction
            const signedTransaction = await TonWalletUtils.signMessage(unsignedTx, seqNo)

            // Step 3: Submit the signed transaction
            const response = await submitPpobEscrowDeposit(signedTransaction, escrowUuid)
            setData(response)
            return response
        } catch (error: unknown) {
            // Check if this is an API error that might contain captcha data
            if (error instanceof ApiError && error.statusCode >= 400 && error.responseBody) {
                const responseBody = error.responseBody;

                // Check if error response contains captcha requirement (explicit flag)
                if (responseBody && typeof responseBody === 'object' && responseBody.captcha_required) {
                    return responseBody as CaptchaRequiredResponse
                }

                // Check if error response contains captcha data (implicit format)
                if (responseBody && typeof responseBody === 'object' && responseBody.captcha_key && responseBody.master_image_base64 && responseBody.thumb_image_base64) {
                    return {
                        captcha_required: true,
                        data: responseBody
                    } as CaptchaRequiredResponse
                }
            }

            Sentry.captureException(error)
            let errorMessage
            if (error instanceof ApiError && error.errorCode) {
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
            } else {
                errorMessage = t('errors.genericClientDescription')
            }
            setError(errorMessage)
            throw errorMessage
        } finally {
            setIsLoading(false)
        }
    }

    const requestPostpaidPayment = async (
        buyerSkuCode: string,
        customerNo: string,
        inquiryQuote: string,
        captchaData?: {
            captcha_key: string,
            captcha_value: { slide_x: number, slide_y: number }
        }
    ) => {
        try {
            setIsLoading(true)

            // Step 1: Create postpaid order and initiate escrow
            const initiateResponse = await client.doPost('/wallet/ppob/postpaid/orders', {
                buyer_sku_code: buyerSkuCode,
                customer_no: customerNo,
                ppob_inquiry_quote_uuid: inquiryQuote,
                ...(captchaData && {
                    captcha_key: captchaData.captcha_key,
                    captcha_value: captchaData.captcha_value
                })
            })

            // Check if response contains captcha requirement
            if (initiateResponse && initiateResponse.captcha_required) {
                return initiateResponse as CaptchaRequiredResponse
            }

            // Check if this is a captcha validation failure response (implicit format)
            if (initiateResponse && initiateResponse.captcha_key && initiateResponse.master_image_base64 && initiateResponse.thumb_image_base64) {
                return {
                    captcha_required: true,
                    data: initiateResponse
                } as CaptchaRequiredResponse
            }

            const { escrow } = initiateResponse.data
            const { escrowUuid, unsignedTx } = escrow
            const seqNo = escrow.additionalData.seqNo

            // Step 2: Sign the transaction
            const signedTransaction = await TonWalletUtils.signMessage(unsignedTx, seqNo)

            // Step 3: Submit the signed transaction
            const response = await submitPpobEscrowDeposit(signedTransaction, escrowUuid)
            setData(response)
            return response
        } catch (error: unknown) {
            // Check if this is an API error that might contain captcha data
            if (error instanceof ApiError && error.statusCode >= 400 && error.responseBody) {
                const responseBody = error.responseBody;

                // Check if error response contains captcha requirement (explicit flag)
                if (responseBody && typeof responseBody === 'object' && responseBody.captcha_required) {
                    return responseBody as CaptchaRequiredResponse
                }

                // Check if error response contains captcha data (implicit format)
                if (responseBody && typeof responseBody === 'object' && responseBody.captcha_key && responseBody.master_image_base64 && responseBody.thumb_image_base64) {
                    return {
                        captcha_required: true,
                        data: responseBody
                    } as CaptchaRequiredResponse
                }
            }

            Sentry.captureException(error)
            let errorMessage
            if (error instanceof ApiError && error.errorCode) {
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')
            } else {
                errorMessage = t('errors.genericClientDescription')
            }
            setError(errorMessage)
            throw errorMessage
        } finally {
            setIsLoading(false)
        }
    }

    return {
        data,
        isLoading,
        error,
        requestBankRedemption,
        requestEWalletRedemption,
        requestPaymentBankRedemption,
        requestP2PTransfer,
        requestPrepaidPayment,
        requestPostpaidPayment
    }
}

export default useManageTransactionRequest
